import { ILead } from './lead.dto';
import {
  IsNotEmpty,
  IsString,
  IsOptional,
  IsDate,
  IsDateString,
  IsObject,
  IsBoolean,
  IsNumber
} from 'class-validator';
import { IBaseResponse } from './base-response.dto';
import { IService } from './service.dto';
import { ISchedule } from './schedule.dto';
import { type Token as StripeToken } from '@stripe/stripe-js';
import type Stripe from 'stripe';
import { IAdminUser } from './crm-user';
import { IHealthCard } from './customer.dto';

export type AppointmentStatus =
  | 'new'
  | 'pending-input'
  | 'confirmed'
  | 'waiting'
  | 'in-progress'
  | 'cancelled'
  | 'completed'
  | 'no-show'
  | 'overflow'
  | 'closed';

export type AppointmentType = 'Physical' | 'Video' | 'Phone';

export class IAppointmentStatusHistory {
  // TODO: there's a swagger runtime error about circular dependency with IStatusHistory<AppointmentStatus>
  prevStatus: AppointmentStatus;
  newStatus: AppointmentStatus;
  updatedAt: Date;
  userId: string;
}

export class IAppointmentMeta {
  referralCode?: string;
  timezone?: string;
  masterId?: string;
  docName?: string;
  [key: string]: any;
}

export class IAppointment {
  organization: string;
  _id?: string;
  accessKey?: string; // for public api
  service: string | IService;
  status: AppointmentStatus;
  relatedToLead?: string | ILead;
  /** reservation date time in UTC */
  reservation?: Date;
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string; // if created from the crm-web app
  schedule?: string | ISchedule; // TODO should be required
  duration: number; // same as service.duration but used here for optimization
  extras: Record<string, any>;
  meta: Record<string, any>;
  virtualMeeting?: IAppointmentVirtualMeeting;
  // reason?: string;
  urgency?: number;
  notes?: string;
  visitReason?: string;
  seenBy?: string | IAdminUser;
  seenAt?: Date;
  statusHistory: IAppointmentStatusHistory[];
  isQueue?: boolean;
  emrId?: string;
  payment?: AppointmentPayment | AppointmentPaymentError;
  cancellationReason?: string;
}

export class AppointmentPaymentError {
  message: string;
}

export class AppointmentPayment {
  type: 'stripeCharge' | 'stripePaymentIntent';
  id: string;
  amount: number;
  currency: string; //'CAD' | 'USD';
  customerId: string;
  status: Stripe.PaymentIntent.Status | Stripe.Charge.Status;
  message: string;
}

export class IAppointmentUpdatedEvent {
  _id: string;
  originalAppointment: IAppointment;
  updatedAppointment: IAppointment;
}

/* -------------------------------------------------------------------------- */
/*                           Create new Appointment                           */
/* -------------------------------------------------------------------------- */

export class CreateAppointmentDto {
  constructor() {}
  @IsNotEmpty()
  @IsString()
  service: string;

  @IsOptional()
  @IsString()
  schedule?: string;

  @IsOptional()
  @IsString()
  status: AppointmentStatus;

  @IsOptional()
  @IsDateString()
  reservation: Date;

  @IsNotEmpty()
  relatedToLead: string | ILead;

  @IsOptional()
  @IsString()
  organization?: string;

  @IsOptional()
  @IsString()
  createdBy?: string;

  @IsOptional()
  @IsObject()
  meta?: IAppointmentMeta;
}

export class CreateAppointmentPublicDto {
  constructor() {}

  @IsNotEmpty()
  @IsString()
  serviceId: string;

  @IsOptional()
  @IsString()
  scheduleId?: string;

  @IsDateString()
  @IsOptional()
  reservation: string;

  @IsOptional()
  @IsObject()
  form: { [key: string]: any };

  @IsOptional()
  @IsObject()
  hc?: IHealthCard;

  @IsOptional()
  @IsObject()
  stripeToken?: StripeToken;

  @IsOptional()
  @IsObject()
  extras?: {
    communicationMethodName?: string;
    primaryIssue?: string;
    receiveMarketing?: boolean;
    dependents?: any[];
    [key: string]: any;
  };

  @IsOptional()
  @IsString()
  uname?: string;

  @IsOptional()
  @IsString()
  organization?: string;

  @IsOptional()
  validateOnly?: boolean;

  @IsOptional()
  @IsBoolean()
  groupBooking?: boolean;

  @IsOptional()
  @IsNumber()
  depsCount?: number;

  @IsOptional()
  @IsNumber()
  qty?: number;

  @IsOptional()
  @IsString()
  referralCode?: string;

  @IsOptional()
  @IsObject()
  meta?: IAppointmentMeta;
}

export class CancelAppointmentByOtpDto {
  constructor() {}

  @IsNotEmpty()
  @IsString()
  code: string;

  @IsNotEmpty()
  @IsString()
  methodId: string;

  @IsNotEmpty()
  @IsString()
  appointmentId: string;
}
export class FindAppointmentPublicDto {
  constructor() {}

  @IsNotEmpty()
  @IsString()
  firstName: string;

  @IsNotEmpty()
  @IsString()
  lastName: string;

  @IsNotEmpty()
  @IsDateString()
  reservation: string;
}

export class CreateAppointmentResponse extends IBaseResponse {
  appointment: IAppointment;
}

/* -------------------------------------------------------------------------- */
/*                               Update Appointment                           */
/* -------------------------------------------------------------------------- */

export class UpdateAppointmentDTO {
  @IsNotEmpty()
  @IsString()
  service?: string;

  @IsNotEmpty()
  @IsString()
  serviceId?: string;

  @IsNotEmpty()
  @IsString()
  serviceType?: string;

  @IsNotEmpty()
  @IsString()
  schedule?: string;

  @IsOptional()
  @IsString()
  status?: AppointmentStatus;

  @IsOptional()
  @IsString()
  cancellationReason?: string;

  @IsOptional()
  @IsDate()
  reservation?: Date;

  @IsOptional()
  relatedToLead?: string | ILead;

  @IsOptional()
  @IsNumber()
  urgency?: number;

  @IsOptional()
  @IsString()
  notes?: string;
}

export class UpdateAppointmentResponse extends IBaseResponse {
  id: string;
}

/* -------------------------------------------------------------------------- */
/*                                Get Appointments                            */
/* -------------------------------------------------------------------------- */
export class GetAppointmentsQueryDto {
  status?: string | string[];
  name?: string;
  service?: string | string[];
  schedule?: string | Object | string[];
  duration?: number;
  reservation?: any;
  createdAt?: any;
  activeOnly?: boolean;
  leadId?: string;
  providerId?: string;
  [key: `meta.${string}`]: string;
  [key: `extras.${string}`]: string;
}

export class IAppointmentFilter {
  _id?: string;
  service?: string | IService;
  status?: AppointmentStatus | { [key: string]: AppointmentStatus } | Object;
  relatedToLead?: string | ILead;
  /** reservation date time in UTC */
  reservation?: Date | Object;
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string; // if created from the crm-web app
  schedule?: string | ISchedule; // TODO should be required
  duration?: number; // same as service.duration but used here for optimization
  extras?: Record<string, any>;
  meta?: Record<string, any>;
}

export type GetAppointmentsResponse = IAppointment[];

export class IAvailableProvider {
  emrId: string;
  name: string;
}

export class IAppointmentVirtualMeeting {
  hostUrl: string;
  joinUrl: string;
}

export class IAppointmentPayloadDTO {
  organization: string;
  _id?: string;
  accessKey?: string; // for public api
  service: string | IService;
  status: AppointmentStatus;
  relatedToLead?: string | ILead;
  /** reservation date time in UTC */
  reservation?: Date;
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string; // if created from the crm-web app
  schedule?: string | ISchedule; // TODO should be required
  duration: number; // same as service.duration but used here for optimization
  extras: Record<string, any>;
  meta: Record<string, any>;
  virtualMeeting?: IAppointmentVirtualMeeting;
  emrId?: string;
  etcReservation: Date;
  reservationDate: string;
  reservationTime: string;
  minutesTillReservation: number;
}

// FROM EMR
export class Day {
  providers: [
    {
      id: string;
      startTime: {
        seconds: number;
        nanoseconds: number;
      };
      endTime: {
        seconds: number;
        nanoseconds: number;
      };
    }
  ];
  bookings: [
    {
      id: string;
      startTime: {
        seconds: number;
        nanoseconds: number;
      };
    }
  ];
}

export class Schedule {
  shifts: [Shift];
}

export class Shift {
  providerId: number;
  startMinute: number;
  endMinute: number;
  services: string[];
}

export class IBooking {
  aptRequestId: string;
  startMinute: number;
  duration: number;
  serviceType: string;
}

export class SelectedTime {
  date: string;
  startMinute: number;
  joinQueue: boolean;
}

export class ContactInfo {
  // registered
  emrId?: number;
  customerId?: string;
  // unregistered
  postal?: string;
  address?: string;
  city?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  dob?: string;
  email?: string;
  phone?: string;
  provinceCode?: string;
  gender?: string;
  allergies?: string;
  temperature?: number;
  weight?: number;
  isChild?: boolean;
  acceptMarketing?: boolean;
}

export class NewDemographicDTO {
  address: {
    postal: string;
    province?: string;
    city: string;
    address: string;
  };
  phone: string;
  email: string;
  firstName: string;
  lastName: string;

  hcNum: string;
  hcType: string;
  hcProv: string;
  hcVer: string;
  hcExp: string;
  hcEff: string;

  dobYear: number;
  dobMonth: number;
  dobDay: number;
  sex: string;
  allergies: string;
  temperature: number;
  weight: number;
  // dateJoined: string;
  // officialLanguage: string;
  // admissionProgramId: number;
  // patientStatus: string;
  // patientStatusDate: string;
  // rosterStatus: string;
}

export class AppointmentPharmacyAddress {
  pharmacyName: string;
  pharmacyPhone: string;
  pharmacyFaxNumber: string;
  pharmacyAddress: string;
}

export class AppointmentRequest {
  startMinute: number;
  date: string;
  service: string;
  serviceId?: string;
  consent: boolean;
  visitReason: string;
  notes?: string;
  pharmacyAddress?: AppointmentPharmacyAddress;
  appointmentType: 'queue' | 'reservation';
  reservationTime?: Date;
}

export class IIdentityDTO {
  emrId: number;
  customerId: string;
}

export class IAppointmentStep {
  key: string;
  stepId: number;
  icon: string;
  nextStepId?: number;
  prevStepId?: number;
  valid?: boolean;
  skip?: boolean;
  handledInComponent?: boolean;
  warning?: string; // warning msg to show when in valid, if null wont show warning and will be handel in the component instead
}

export class IUpdateAppointmentStep {
  key: string;
  valid?: boolean;
  skip?: boolean;
  handledInComponent?: boolean;
  warning?: string; // warning msg to show when in valid, if null wont show warning and will be handel in the component instead
}

export class SelectedService {
  _id: string;
  label: string;
  extras?: any;
  description?: string;
  isHidden?: boolean;
  status: string;
}
